<script lang="ts" setup>
import { NInput, useMessage } from "naive-ui";
import { ref, watch, nextTick, computed } from "vue";

const message = useMessage();

const props = withDefaults(
    defineProps<{
        modelValue: string | null | undefined;
        placeholder?: string;
        livePreview?: boolean;
        clearable?: boolean;
        showSuffix?: boolean;
        showPrefix?: boolean;
        disabled?: boolean;
        showCopyBtn?: boolean;
        // if data is empty on touch (focus+blur), set status to error
        validateStatusOnTouch?: boolean;
        status?: "success" | "error" | "warning";
        // if true, only emit update on blur/enter, not while typing
        // useful to avoid sending multiple request to API when value changes
        disableUpdateOnChange?: boolean;
        bordered?: boolean;
        // if true, never show border (even in live preview mode)
        forceNotBorder?: boolean;
        // Enable multiline/wrapping input
        multiline?: boolean;
        size?: "normal" | "medium" | "large";
    }>(),
    {
        modelValue: null,
        placeholder: "",
        livePreview: false,
        clearable: false,
        showSuffix: false,
        showPrefix: false,
        disabled: false,
        showCopyBtn: false,
        validateStatusOnTouch: false,
        status: undefined,
        disableUpdateOnChange: false,
        bordered: true,
        forceNotBorder: false,
        multiline: false,
        size: "normal",
    },
);

const emits = defineEmits<{
    (_e: "update:modelValue", _value: string | null | undefined): void;
    (_e: "clear"): void;
}>();

const isEditing = ref(false);
const localValue = ref(props.modelValue);
const inputRef = ref<any>(null);
const localStatus = ref(props.status);
const touched = ref(false);

function handleFocus() {
    touched.value = true;
}

function handleBlur() {
    if (
        props.validateStatusOnTouch &&
        touched.value &&
        (localValue.value === null || localValue.value === undefined || localValue.value === "")
    ) {
        localStatus.value = "error";
    } else {
        localStatus.value = props.status;
    }
    exitEditMode();
}

const computedStatus = computed(() => {
    return localStatus.value;
});

watch(
    () => props.modelValue,
    (val) => {
        // keep local in sync unless user currently editing in preview mode
        if (!isEditing.value) {
            localValue.value = val;
        }
    },
);

function enterEditMode() {
    isEditing.value = true;
    touched.value = true;
    nextTick(() => {
        inputRef.value?.focus();
    });
}

function emitCommitIfChanged() {
    if (localValue.value !== props.modelValue) {
        emits("update:modelValue", localValue.value);
    }
}

function exitEditMode() {
    // If livePreview, only commit when actually exiting edit mode
    if (props.livePreview) {
        if (!isEditing.value) return; // not in edit mode yet
        isEditing.value = false;
    }
    // When updates are disabled during typing, commit now
    if (props.disableUpdateOnChange) {
        emitCommitIfChanged();
    }
}

function handleKeydown(e: KeyboardEvent) {
    if (e.key === "Enter") {
        // Multiline behavior: Enter commits (no newline), Shift+Enter inserts newline
        if (props.multiline) {
            if (!e.shiftKey) {
                // Prevent newline insertion and commit/exit edit mode
                e.preventDefault();
                exitEditMode();
            }
            // If Shift+Enter, allow default newline and do NOT commit yet
            return;
        }
        // Single line behavior (unchanged)
        exitEditMode();
    }
}

function handleInput(value: string) {
    localValue.value = value;
    // Emit live updates only if disableUpdateOnChange is false
    if (!props.disableUpdateOnChange) {
        emits("update:modelValue", value);
    }
}

function onClearFunc() {
    localValue.value = null;
    emits("clear");
}

const isCopyValid = computed(() => {
    return localValue.value !== null && localValue.value !== undefined && localValue.value !== "";
});

function handleCopyLabel() {
    if (!isCopyValid.value) return;
    navigator.clipboard.writeText(localValue.value || "");
    message.info("Copied!", {
        duration: 1000,
    });
}

const renderOutline = computed(() => {
    return !props.bordered && !props.livePreview && !props.forceNotBorder;
});

// Size-based class helpers
const inputSizeClass = computed(() => {
    switch (props.size) {
        case "large":
            return "!text-3xl !font-bold py-2";
        case "medium":
            return "!text-xl !font-semibold py-1.5";
        default:
            return "";
    }
});

const previewSizeClass = computed(() => {
    switch (props.size) {
        case "large":
            return "min-h-[52px] !text-3xl !font-bold";
        case "medium":
            return "min-h-[42px] !text-xl !font-semibold";
        default:
            return "min-h-[34px]";
    }
});
</script>

<template>
    <div class="relative w-full text-left">
        <n-input
            v-if="!livePreview || isEditing"
            ref="inputRef"
            v-model:value="localValue"
            :placeholder="placeholder"
            :type="multiline ? 'textarea' : 'text'"
            :autosize="multiline"
            class="w-full"
            :class="[
                inputSizeClass,
                renderOutline
                    ? 'outline outline-[1px] outline-transparent hover:outline-gray-300 focus-within:outline-blurple-100 focus-within:hover:outline-blurple-100 active:outline-blurple-100'
                    : '',
            ]"
            round
            :bordered="bordered"
            :status="computedStatus"
            :clearable="clearable"
            :disabled="disabled"
            @focus="handleFocus"
            @blur="handleBlur"
            @keydown="handleKeydown"
            @input="handleInput"
            @clear="onClearFunc"
        >
            <template v-if="showSuffix" #suffix>
                <span class="material-symbols-outlined text-slate-300">search</span>
            </template>
            <template v-if="showPrefix" #prefix>
                <span class="material-symbols-outlined text-slate-300">search</span>
            </template>
        </n-input>
        <p
            v-else
            :class="[
                'cursor-pointer flex items-center px-3 rounded-[2em] w-full border border-transparent hover:bg-cream transition-colors duration-150',
                previewSizeClass,
            ]"
            @click="enterEditMode"
        >
            {{ localValue || placeholder || "-" }}
        </p>
        <button
            v-if="showCopyBtn"
            :class="[
                'material-symbols-outlined text-gray-300 text-lg absolute top-0.5 right-7 z-10',
                !isCopyValid ? 'cursor-not-allowed opacity-50' : 'active:scale-95',
            ]"
            :disabled="!isCopyValid"
            @click="handleCopyLabel"
        >
            content_copy
        </button>
    </div>
</template>
